// One entry per service. Adding a service, project or screenshot only means editing this file.
//
// PROJECT SCREENSHOTS: put images in public/projects/ and list them under `images`, in the order visitors should see them.
// Recommended size: 1600 x 900 px (16:9), JPG or WebP under 300 KB each. Any number of images works (the first one is the cover).
// `link` is where "View on MQL5 Market" goes. For a Market page, paste the product URL; visitors see the full description, versions and reviews there.
// A project with no images still shows as a text card, and the "View Projects" button on the landing page appears
// only for services that have at least one project below.
//
// Template for a new project:
// { title: '', desc: '', highlights: ['', ''], tags: [''],
//   images: [{ src: '/projects/xxx-1.jpg', caption: '' }, { src: '/projects/xxx-2.jpg', caption: '' }],
//   link: 'https://www.mql5.com/en/market/product/...', linkLabel: 'View on MQL5 Market' },

export const SERVICES = [
  {
    key: 'bot', slug: 'trading-bots', name: 'Trading bots & EAs', group: 'For traders',
    title: 'Trading bots and Expert Advisors, built to your rules.',
    sub: 'Automated strategies for MT4, MT5 and cTrader, backtested and optimised.',
    stack: ['MQL4/5', 'Python', 'C# (cTrader)'],
    intro: 'Your strategy turned into a fully automated system, backtested and optimised, with your risk rules built into the execution.',
    who: 'Traders, trading educators and signal providers who follow clear rules and want them executed automatically.',
    included: [
      ['Written specification', 'Every entry, exit, filter and risk rule in plain language, approved by you before any code is written.'],
      ['The bot or Expert Advisor', 'Built for MT4, MT5 or cTrader, with adjustable inputs for risk, stops, sessions and instruments.'],
      ['Backtest and optimisation report', 'Settings documented so you can repeat the tests yourself.'],
    ],
    faq: [
      ['Which platforms do you build for?', 'MetaTrader 4 and 5 and cTrader, plus Python bots for exchanges.'],
      ['Can you fix an existing EA?', 'Yes. We review the code and the live-trade behaviour first, then quote the fix.'],
    ],
    featured: { label: 'Packaged offer', text: 'Have written rules and want a fixed price and delivery date? See the Strategy Automation build.', to: '/strategy-automation', cta: 'See Strategy Automation' },
    projects: [
      { title: 'Q-Trader EA', desc: 'MT5 Expert Advisor for XAUUSD with risk-based position sizing, published on MQL5 Market with live results on Myfxbook.', tags: ['MQL5', 'XAUUSD', 'Live results'], images: [], link: '', linkLabel: 'View on MQL5 Market' },
      { title: 'Client MT4 Expert Advisors', desc: 'Custom MT4 EAs and live-trade fixes built for clients on Upwork and Freelancer.com, from complete builds to entry-logic repairs.', tags: ['MQL4', 'MetaTrader', 'Client work'], images: [], link: '', linkLabel: 'View project' },
    ],
  },
  {
    key: 'indicator', slug: 'indicators-alerts', name: 'Indicators & alerts', group: 'For traders',
    title: 'Custom indicators and alerts that show your analysis the way you read it.',
    sub: 'Built in Pine Script and MQL.',
    stack: ['Pine Script', 'MQL4/5', 'C#'],
    intro: 'Custom indicators and alert systems that put your analysis on the chart the way you read it.',
    who: 'Discretionary traders and educators who want their method visible on the chart and alerts when it appears.',
    included: [
      ['Indicator logic specification', 'Your method written out and confirmed before the build.'],
      ['The indicator', 'Built in Pine Script, MQL4/5 or C# for the platform you trade on.'],
      ['Alerts', 'Alerts through the channels your platform supports, set up and tested.'],
    ],
    faq: [
      ['Can you port an indicator between platforms?', 'Usually yes. The logic is rebuilt and checked against the original on the same data.'],
      ['Can an indicator become a bot later?', 'Yes. A well-specified indicator is a good starting point for an automated version.'],
    ],
    projects: [],
  },
  {
    key: 'ui', slug: 'trade-assistant-uis', name: 'Trade assistant UIs', group: 'For traders',
    title: 'On-chart trade panels for sizing, orders and risk.',
    sub: 'Everything a manual trader needs in one view.',
    stack: ['MQL5', 'C#', 'Python'],
    intro: 'On-chart panels for manual traders: position sizing, order placement, trade management and live risk in one view.',
    who: 'Manual traders who want faster, safer execution without giving up control of their decisions.',
    included: [
      ['Panel design around your workflow', 'The controls you use, laid out the way you use them.'],
      ['Sizing and order tools', 'Position sizing from risk, one-click orders, partial closes, break-even and trailing rules.'],
      ['Live risk display', 'Exposure and risk shown before and after you place a trade.'],
    ],
    faq: [
      ['Does the panel trade by itself?', 'No. You make every decision. The panel only makes execution faster and more consistent.'],
      ['Can you add features to my existing panel?', 'Yes. We review it first and quote the additions.'],
    ],
    projects: [
      { title: 'Q-Panel', desc: 'MT5 trade management panel covering position sizing, partial closes, break-even and trailing rules, published on MQL5 Market.', highlights: ['Position sizing', 'Partial closes', 'Break-even', 'Trailing rules'], tags: ['MQL5', 'Trade panel'], images: [], link: '', linkLabel: 'View on MQL5 Market' },
    ],
  },
  {
    key: 'exchange', slug: 'exchange-bots', name: 'Binance & exchange bots', group: 'Crypto and DeFi',
    title: 'Grid, DCA and market-making bots for Binance and other exchanges.',
    sub: 'Built around exchange API limits and your risk controls.',
    stack: ['Python', 'Exchange APIs', 'WebSockets'],
    intro: 'Automated trading on Binance and other major exchanges: grid, DCA, market-making and signal-driven bots, built around exchange API limits and your risk controls.',
    who: 'Crypto traders and desks who want a strategy running around the clock with limits they control.',
    included: [
      ['Strategy and risk specification', 'Order logic, position limits and stop conditions agreed before the build.'],
      ['The bot', 'Python, built around the exchange API, rate limits and reconnect handling.'],
      ['Testnet or paper run and deployment guide', 'Tested before real funds are involved, with a guide to run it yourself.'],
    ],
    faq: [
      ['Do you hold my API keys or funds?', 'No. Keys stay with you. We recommend trade-only permissions with withdrawals disabled.'],
      ['Which exchanges do you support?', 'Binance and other major exchanges with a public API. Tell us which one on the call.'],
    ],
    projects: [],
  },
  {
    key: 'arb', slug: 'arbitrage-bots', name: 'Arbitrage bots', group: 'Crypto and DeFi',
    title: 'Arbitrage scanners and bots with real costs priced in.',
    sub: 'Gaps across exchanges, DEXs and P2P.',
    stack: ['Python', 'CEX / DEX', 'P2P'],
    intro: 'Scanners and bots that find and act on price gaps between exchanges, DEXs and P2P markets, with fees, slippage and transfer time priced in.',
    who: 'Traders and small desks who want to find gaps faster than they can by hand.',
    included: [
      ['Opportunity scanner', 'Prices compared across the venues you choose, with fees and slippage included.'],
      ['Alerts or automated execution', 'Notify you, or act within the limits you set.'],
      ['Dashboard and deployment guide', 'A clear view of what the system found and did, plus setup help.'],
    ],
    faq: [
      ['Is arbitrage risk-free?', 'No. Gaps can close before an order fills, and transfers take time. The system prices these risks in but cannot remove them.'],
      ['Which venues can you connect?', 'Centralised exchanges, DEXs and P2P markets with accessible APIs. We confirm feasibility on the call.'],
    ],
    projects: [
      { title: 'Arbitrage platform', desc: 'Python and FastAPI back end with exchange, DeFi, OTC and remittance scanners, PostgreSQL migrations and Docker, plus a Next.js and TypeScript front end.', tags: ['Python', 'FastAPI', 'Next.js'], images: [], link: '', linkLabel: 'View project' },
    ],
  },
  {
    key: 'contract', slug: 'smart-contracts', name: 'Smart contracts', group: 'Crypto and DeFi',
    title: 'Solidity smart contracts, tested before anything is deployed.',
    sub: 'Tokens, staking and custom on-chain logic.',
    stack: ['Solidity', 'EVM'],
    intro: 'Smart contracts for tokens, staking and custom on-chain logic, written and tested on testnet before anything is deployed.',
    who: 'Founders and teams who need on-chain logic written clearly and tested properly.',
    included: [
      ['Contract specification', 'What the contract does, who can call what, and every edge case, agreed up front.'],
      ['Solidity contracts and tests', 'Written for EVM chains with a test suite.'],
      ['Testnet deployment', 'Deployed and exercised on testnet, with a deployment guide for mainnet.'],
    ],
    faq: [
      ['Do you offer security audits?', 'We test thoroughly on testnet, but a contract that holds significant funds should also get an independent security audit.'],
      ['Which chains do you support?', 'EVM-compatible chains. Tell us which one on the call.'],
    ],
    projects: [],
  },
  {
    key: 'hft', slug: 'hft-systems', name: 'HFT & institutional systems', group: 'For firms and desks',
    title: 'Low-latency execution and order routing for firms and desks.',
    sub: 'Scoped around your venues and latency targets.',
    stack: ['C++', 'C#', 'Python'],
    intro: 'Low-latency execution engines, order routing and market data handling, scoped around your venues, latency targets and infrastructure.',
    who: 'Prop desks, funds and fintech teams with specific venue and latency requirements.',
    included: [
      ['Technical scoping', 'Venues, latency targets, infrastructure and risk controls written down before any build.'],
      ['Execution and routing components', 'Built in C++, C# or Python to the agreed targets.'],
      ['Testing and handover', 'Performance tested against the targets, with documentation for your team.'],
    ],
    faq: [
      ['How is this priced?', 'Scoped per project after a discovery call, because it depends on venues and latency targets.'],
      ['Can you work with our infrastructure?', 'Yes. We scope around what you already run.'],
    ],
    projects: [],
  },
];

export const SERVICE_BY_KEY = Object.fromEntries(SERVICES.map((s) => [s.key, s]));
export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));